import styled from "styled-components";
import { Bio } from "../../data/constants";
import {
  Section,
  Eyebrow,
  SectionTitle,
  Head,
  Glass,
  Reveal,
  useReveal,
} from "../../studio/ui";
import { Vesica } from "../../studio/SacredGeometry";

/* ============================================================
   ABOUT — Divine Futurism. Sacred geometry behind an
   obsidian void; celestial-gold heading, frosted quick-facts.
============================================================ */

const Frame = styled.div`
  position: relative;
`;

/* faint vesica-piscis decoration drifting behind the content */
const Halo = styled.div`
  position: absolute;
  top: -60px;
  right: -40px;
  pointer-events: none;
  z-index: 0;

  @media ${({ theme }) => theme.bp.lg} {
    top: -30px;
    right: -20px;
    opacity: 0.7;
  }
`;

const Grid = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 56px;
  align-items: start;

  @media ${({ theme }) => theme.bp.lg} {
    grid-template-columns: 1fr;
    gap: 36px;
  }
`;

const Copy = styled.p`
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 17px;
  line-height: 1.75;
  color: ${({ theme }) => theme.textMuted};

  & + & {
    margin-top: 18px;
  }

  strong {
    color: ${({ theme }) => theme.white};
    font-weight: 600;
  }
`;

const Facts = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media ${({ theme }) => theme.bp.sm} {
    grid-template-columns: 1fr 1fr;
  }
`;

const Fact = styled(Glass)`
  padding: 22px 20px;

  .v {
    font-family: ${({ theme }) => theme.fonts.serif};
    font-size: 28px;
    line-height: 1.05;
    font-weight: 600;
    letter-spacing: -0.01em;
    background: linear-gradient(
      120deg,
      ${({ theme }) => theme.goldSoft},
      ${({ theme }) => theme.gold}
    );
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: ${({ theme }) => theme.gold};
  }

  .k {
    margin-top: 8px;
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: 13px;
    line-height: 1.5;
    letter-spacing: 0.01em;
    color: ${({ theme }) => theme.textFaint};
  }
`;

const FACTS = [
  { v: "1+", k: "Years building software" },
  { v: "5+", k: "Shipped projects" },
  { v: "ClickHouse", k: "Large-scale analytics" },
  { v: "AWS SAA", k: "Certified · 2025" },
];

export default function About() {
  const [ref, shown] = useReveal(0.12);

  return (
    <Section id="about">
      <Frame>
        <Halo aria-hidden="true">
          <Vesica size={260} />
        </Halo>

        <Head>
          <Eyebrow>About</Eyebrow>
          <SectionTitle>
            Engineer who <em>ships</em>, end to end
          </SectionTitle>
        </Head>

        <Reveal ref={ref} $in={shown} $delay={0}>
          <Grid>
            <div>
              <Copy>{Bio.description}</Copy>
              <Copy>
                I own features <strong>end to end</strong> — designing schemas
                and tuning queries on the backend, then shipping the polished{" "}
                <strong>React</strong> front-end that sits on top. I write{" "}
                <strong>clean, maintainable code</strong> and care as much about
                the details users feel as the systems they never see.
              </Copy>
            </div>

            <Facts>
              {FACTS.map((f) => (
                <Fact key={f.k}>
                  <div className="v">{f.v}</div>
                  <div className="k">{f.k}</div>
                </Fact>
              ))}
            </Facts>
          </Grid>
        </Reveal>
      </Frame>
    </Section>
  );
}
